/**
 * CajeroDashboard (F87) — vista personalizada para users con rol "cajero".
 *
 * Solo muestra info de SU operación, no cifras agregadas del negocio:
 *   - Mi recaudación HOY (monto + count)
 *   - Mi recaudación MES
 *   - Botón grande "Aplicar pago rápido" (atajo Ctrl+K)
 *   - Mis últimos 20 pagos (30 días)
 *
 * rc32 (2026-05-19): se removió "Próximos a vencer" — el cajero no hace
 * cobranza activa, solo registra pagos cuando el cliente llega al punto
 * de pago físico.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Wallet, ArrowRight, RefreshCw, Loader2, Plus, Printer,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { apiCall } from "@/lib/api";
import { formatMXN, formatDateTime } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
import { PagoRapidoDialog } from "@/features/pago-rapido/PagoRapidoDialog";
import { abrirReciboPago } from "@/features/config/api";

type CajeroDashboardData = {
  fecha: string;
  mi_recaudacion_hoy: { monto: string; count: number };
  mi_recaudacion_mes: { monto: string; count: number };
  mis_ultimos_pagos: Array<{
    pago_id: string;
    folio: string;
    cliente_id: string;
    cliente_nombre: string;
    monto: string;
    metodo: string;
    created_at: string;
  }>;
};

function useCajeroDashboard() {
  return useQuery({
    queryKey: ["dashboard", "cajero"],
    queryFn: () => apiCall<CajeroDashboardData>("get", "/api/v1/dashboard/cajero"),
    refetchInterval: 30_000,
  });
}

export function CajeroDashboard() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading, isFetching, refetch } = useCajeroDashboard();
  const [pagoOpen, setPagoOpen] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  const hoyMonto = Number(data.mi_recaudacion_hoy.monto);
  const mesMonto = Number(data.mi_recaudacion_mes.monto);
  const nombre = user?.nombre_completo || user?.email?.split("@")[0] || "cajero";

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Hola, {nombre} 👋</h2>
          <p className="text-xs text-muted-foreground">
            Tu caja del día · {data.fecha}
            {isFetching && " · actualizando…"}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} />
          Refrescar
        </Button>
      </div>

      {/* CTA grande: aplicar pago */}
      <button
        onClick={() => setPagoOpen(true)}
        className="flex items-center justify-between rounded-xl border-2 border-primary/40 bg-primary/5 p-5 text-left transition hover:bg-primary/10 hover:border-primary"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Plus size={22} />
          </div>
          <div>
            <div className="text-base font-semibold">Aplicar pago rápido</div>
            <div className="text-xs text-muted-foreground">
              Busca al cliente y registra su pago en segundos
              <kbd className="ml-2 inline-flex h-4 items-center rounded border bg-muted px-1.5 text-[9px]">
                ⌘K
              </kbd>
            </div>
          </div>
        </div>
        <ArrowRight size={18} className="text-primary" />
      </button>

      {/* Mis 2 KPIs personales */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border bg-card p-4">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Mi recaudación hoy
          </div>
          <div className="mt-1 text-3xl font-semibold font-mono text-emerald-600 dark:text-emerald-400">
            {formatMXN(hoyMonto)}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {data.mi_recaudacion_hoy.count} pago{data.mi_recaudacion_hoy.count !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Mi recaudación del mes
          </div>
          <div className="mt-1 text-3xl font-semibold font-mono">
            {formatMXN(mesMonto)}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {data.mi_recaudacion_mes.count} pago{data.mi_recaudacion_mes.count !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Mis últimos pagos */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center gap-2 border-b p-3">
          {/* rc40: removido link "Ver todos → /pagos" porque exponía la lista
              global admin (con búsqueda de clientes y acciones admin). El cajero
              ya ve sus últimos pagos aquí, no necesita la lista global. */}
          <Wallet size={14} />
          <h3 className="text-sm font-semibold">Mis últimos pagos</h3>
        </div>
        {data.mis_ultimos_pagos.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            Aún no tienes pagos registrados. Empieza con el botón de arriba.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Folio</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Método</TableHead>
                <TableHead>Hora</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="w-12 pr-4"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.mis_ultimos_pagos.map((p) => (
                <TableRow key={p.pago_id}>
                  <TableCell className="font-mono text-xs">{p.folio}</TableCell>
                  <TableCell>
                    {/* rc34: cajero NO navega al cliente desde aquí —
                        solo necesita ver que cobró. Las acciones admin
                        del cliente (suspender/editar/etc) no son su trabajo. */}
                    <span className="text-xs">{p.cliente_nombre}</span>
                  </TableCell>
                  <TableCell className="capitalize text-xs">{p.metodo}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDateTime(p.created_at)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatMXN(Number(p.monto))}
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    {/* rc37: reimprimir recibo del pago */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Imprimir recibo"
                      onClick={() => {
                        abrirReciboPago(p.pago_id).catch((err) =>
                          toast.error("Error al abrir recibo", { description: err.message })
                        );
                      }}
                    >
                      <Printer size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Dialog Pago Rápido */}
      <PagoRapidoDialog open={pagoOpen} onOpenChange={setPagoOpen} />
    </div>
  );
}
